import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { armadilloSecurity } from "@/security/armadillo";

export type AssuranceStatus = "checking" | "verified" | "mfa_required" | "error" | "signed_out";

function recoveryLocation(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("reset") === "true"
    || new URLSearchParams(window.location.hash.slice(1)).get("type") === "recovery";
}

// Retain only the flow type, never a copy of callback tokens.
const INITIAL_RECOVERY = recoveryLocation();

interface AuthCtx {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  assuranceStatus: AssuranceStatus;
  isPasswordRecovery: boolean;
  completePasswordRecovery: () => void;
  refreshAssurance: () => Promise<boolean>;
}

const Ctx = createContext<AuthCtx>({
  user: null, session: null, loading: true, signOut: async () => {}, isAuthenticated: false,
  assuranceStatus: "checking", isPasswordRecovery: false,
  completePasswordRecovery: () => {}, refreshAssurance: async () => false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [, setLastActivity] = useState(Date.now());
  const lastActivityRef = useRef(Date.now());
  const [assuranceStatus, setAssuranceStatus] = useState<AssuranceStatus>("checking");
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(() => INITIAL_RECOVERY || recoveryLocation());
  const [authRevision, setAuthRevision] = useState(0);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const assuranceRequest = useRef(0);
  const assuredToken = useRef<string | null>(null);
  const authEventRevision = useRef(0);
  const completePasswordRecovery = useCallback(() => setIsPasswordRecovery(false), []);

  const refreshAssurance = useCallback(async () => {
    const request = ++assuranceRequest.current;
    const current = sessionRef.current;
    assuredToken.current = null;
    if (!current) {
      setAssuranceStatus("signed_out");
      return false;
    }
    setAssuranceStatus("checking");
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (request !== assuranceRequest.current || sessionRef.current?.access_token !== current.access_token) return false;
      if (error || !data?.currentLevel || !data.nextLevel
        || (current.expires_at && current.expires_at * 1000 <= Date.now())) throw new Error("Assurance unavailable");
      const verified = data.currentLevel === "aal2" || data.nextLevel === "aal1";
      assuredToken.current = current.access_token;
      setAssuranceStatus(verified ? "verified" : "mfa_required");
      return verified;
    } catch {
      if (request === assuranceRequest.current) setAssuranceStatus("error");
      return false;
    }
  }, []);

  useEffect(() => {
    void refreshAssurance();
    return () => { assuranceRequest.current += 1; };
  }, [session, authRevision, refreshAssurance]);

  const applySession = useCallback((next: Session | null) => {
    assuranceRequest.current += 1;
    assuredToken.current = null;
    sessionRef.current = next;
    setAssuranceStatus(next ? "checking" : "signed_out");
    setAuthRevision(revision => revision + 1);
    setSession(next);
    setUser(next?.user ?? null);
    setIsAuthenticated(!!next?.user);
    lastActivityRef.current = Date.now();
    try {
      localStorage.removeItem("mfa_remembered");
      if (next) localStorage.setItem("last_activity", Date.now().toString());
      else localStorage.removeItem("last_activity");
    } catch { /* Optional activity hints must not break authentication. */ }
  }, []);

  useEffect(() => {
    let active = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, next) => {
      if (!active) return;
      authEventRevision.current += 1;
      if (event === "PASSWORD_RECOVERY") setIsPasswordRecovery(true);
      // SDK calls are deferred to the effect above, outside the auth callback lock.
      applySession(next);
      setLoading(false);
    });
    const revision = authEventRevision.current;
    const checkSession = async () => {
      try {
        const { data: { session: next }, error } = await supabase.auth.getSession();
        if (!active || revision !== authEventRevision.current) return;
        if (error) throw error;
        if (next?.expires_at && next.expires_at * 1000 <= Date.now()) {
          applySession(null);
          await supabase.auth.signOut({ scope: "local" });
          return;
        }
        applySession(next);
      } catch {
        if (active && revision === authEventRevision.current) {
          applySession(null);
          setAssuranceStatus("error");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    void checkSession();
    return () => {
      active = false;
      authEventRevision.current += 1;
      subscription.unsubscribe();
    };
  }, [applySession]);

  useEffect(() => {
    if (user?.id) void armadilloSecurity.initialize(user.id).catch(() => {});
  }, [user?.id]);

  const sessionUserId = session?.user.id;

  // Separate effect for session refresh interval
  useEffect(() => {
    if (!sessionUserId) return;

    // Refresh session every 10 minutes to prevent expiration
    let active = true;
    const refreshInterval = setInterval(async () => {
      const revision = authEventRevision.current;
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!active || revision !== authEventRevision.current) return;
        if (error) throw error;
        applySession(data.session);
      } catch {
        if (active && revision === authEventRevision.current) {
          assuranceRequest.current += 1;
          assuredToken.current = null;
          setAssuranceStatus("error");
        }
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => {
      active = false;
      clearInterval(refreshInterval);
    };
  }, [sessionUserId, applySession]);

  // Track user activity for security
  useEffect(() => {
    if (!session?.user) return;

    const updateActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      setLastActivity(now);
      try { localStorage.setItem('last_activity', now.toString()); } catch { /* Optional hint. */ }
    };

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Check for inactivity every minute
    const inactivityCheck = setInterval(() => {
      const now = Date.now();
      const thirtyMinutes = 30 * 60 * 1000;
      
      if (now - lastActivityRef.current > thirtyMinutes) {
        // Clear MFA remember token after 30 minutes of inactivity
        try { localStorage.removeItem('mfa_remembered'); } catch { /* Never used as assurance. */ }
      }
    }, 60 * 1000); // Check every minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(inactivityCheck);
    };
  }, [session?.user]);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    applySession(null);
    setIsPasswordRecovery(false);
  };

  const assurancePending = !!session && (assuranceStatus === "checking"
    || (assuranceStatus !== "error" && assuredToken.current !== session.access_token));
  return <Ctx.Provider value={{
    user, session, loading: loading || assurancePending, signOut,
    isAuthenticated: isAuthenticated && assuranceStatus === "verified" && !assurancePending && !isPasswordRecovery,
    assuranceStatus, isPasswordRecovery, completePasswordRecovery, refreshAssurance,
  }}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);